// File: frontend/src/pages/attendance/AttendanceReport.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import api from '../../utils/axios';

const AttendanceReport = () => {
  const { school } = useSchool();
  const [selectedClass, setSelectedClass] = useState('1');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch attendance report for all students in class for specified month/year
   */
  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch class student roster
      const rosterResponse = await api.get(`/api/v1/attendance/class/${selectedClass}`, {
        params: { date: `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01` }
      });

      if (!rosterResponse.data?.success) {
        setError('Failed to fetch class roster');
        setLoading(false);
        return;
      }

      const students = rosterResponse.data.data?.students || [];

      // Fetch individual attendance history for each student in parallel
      const reportPromises = students.map(async (student) => {
        try {
          const res = await api.get(`/api/v1/attendance/student/${student.student_id}`, {
            params: { month: selectedMonth, year: selectedYear }
          });
          if (res.data?.success && res.data.data?.summary) {
            return {
              student_id: student.student_id,
              student_name: student.student_name,
              father_name: student.father_name,
              roll_no: student.roll_no,
              ...res.data.data.summary
            };
          }
        } catch (err) {
          console.error(`Error loading report for student ${student.student_id}:`, err);
        }
        return {
          student_id: student.student_id,
          student_name: student.student_name,
          father_name: student.father_name,
          roll_no: student.roll_no,
          total_days: 0,
          present: 0,
          absent: 0,
          late: 0,
          leave: 0,
          percentage: 100
        };
      });

      const reportResults = await Promise.all(reportPromises);
      setReportData(reportResults);
    } catch (err) {
      console.error('Error fetching attendance report:', err);
      setError(err.response?.data?.message || err.message || 'Error generating report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedMonth, selectedYear]);

  /**
   * Return color badge for attendance percentage
   */
  const getPercentageBadge = (pct) => {
    if (pct >= 90) {
      return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
    } else if (pct >= 75) {
      return 'bg-amber-100 text-amber-800 border border-amber-200';
    } else {
      return 'bg-red-100 text-red-800 border border-red-200';
    }
  };

  /**
   * Export attendance report data as downloadable CSV file
   */
  const handleExportCSV = () => {
    if (!reportData || reportData.length === 0) {
      alert('No attendance data available to export.');
      return;
    }

    const headers = ['Roll No', 'Student ID', 'Student Name', 'Father Name', 'Total Days', 'Present', 'Absent', 'Late', 'Leave', 'Percentage (%)'];
    const rows = reportData.map((row) => [
      `"${row.roll_no || ''}"`,
      `"${row.student_id || ''}"`,
      `"${row.student_name || ''}"`,
      `"${row.father_name || ''}"`,
      row.total_days || 0,
      row.present || 0,
      row.absent || 0,
      row.late || 0,
      row.leave || 0,
      `"${row.percentage || 100}%"`
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Attendance_Report_Class_${selectedClass}_Month_${selectedMonth}_Year_${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard/admin" className="w-10 h-10 rounded-xl overflow-hidden bg-white border border-slate-200 p-1 flex items-center justify-center shadow-sm">
              {school?.logo ? (
                <img src={school.logo} alt="Logo" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="font-extrabold text-primary text-sm">SMS</span>
              )}
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">{school?.name || 'Attendance Report'}</h1>
              <p className="text-xs text-slate-500">Monthly attendance performance & statistics</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/attendance/mark"
              className="bg-primary hover:bg-blue-600 text-white font-medium px-4 py-2 rounded-xl text-sm transition shadow-sm"
            >
              Mark Attendance
            </Link>
            <Link
              to="/attendance/summary"
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-xl text-sm transition"
            >
              Summary Overview
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Filter Controls */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4 items-end">
            
            {/* Filter Class */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Class</label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="1">Class 1</option>
                <option value="2">Class 2</option>
                <option value="3">Class 3</option>
                <option value="4">Class 4</option>
                <option value="5">Class 5</option>
                <option value="6">Class 6</option>
                <option value="7">Class 7</option>
                <option value="8">Class 8</option>
                <option value="9">Class 9</option>
                <option value="10">Class 10</option>
                <option value="11">Class 11</option>
                <option value="12">Class 12</option>
              </select>
            </div>

            {/* Filter Month */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value={1}>January</option>
                <option value={2}>February</option>
                <option value={3}>March</option>
                <option value={4}>April</option>
                <option value={5}>May</option>
                <option value={6}>June</option>
                <option value={7}>July</option>
                <option value={8}>August</option>
                <option value={9}>September</option>
                <option value={10}>October</option>
                <option value={11}>November</option>
                <option value={12}>December</option>
              </select>
            </div>

            {/* Filter Year */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
                <option value={2027}>2027</option>
              </select>
            </div>

            {/* Export Button */}
            <div>
              <button
                type="button"
                onClick={handleExportCSV}
                className="w-full py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-semibold rounded-xl text-sm shadow-sm transition flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Report
              </button>
            </div>

          </div>
        </div>

        {/* Error Notification */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-800">✕</button>
          </div>
        )}

        {/* Report Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-bold text-slate-800">
              Class {selectedClass} • Month: <span className="text-primary font-bold">{selectedMonth}/{selectedYear}</span>
            </div>
            <div className="text-xs text-slate-500">
              Legend: <span className="text-emerald-700 font-bold">≥90% Excellent</span> | <span className="text-amber-700 font-bold">75-89% Average</span> | <span className="text-red-700 font-bold">&lt;75% Low</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-3.5">Roll No</th>
                  <th className="px-6 py-3.5">Student Name</th>
                  <th className="px-6 py-3.5">Total Days</th>
                  <th className="px-6 py-3.5 text-emerald-700">Present</th>
                  <th className="px-6 py-3.5 text-red-700">Absent</th>
                  <th className="px-6 py-3.5 text-amber-700">Late</th>
                  <th className="px-6 py-3.5 text-blue-700">Leave</th>
                  <th className="px-6 py-3.5 text-right">Attendance %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm font-medium text-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-slate-400">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-8 h-8 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Generating report statistics...</span>
                      </div>
                    </td>
                  </tr>
                ) : reportData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-slate-500">
                      No attendance data found for Class {selectedClass} in {selectedMonth}/{selectedYear}.
                    </td>
                  </tr>
                ) : (
                  reportData.map((row) => (
                    <tr key={row.student_id} className="hover:bg-slate-50/80 transition">
                      <td className="px-6 py-4 font-mono font-bold text-slate-800 text-xs">
                        {row.roll_no || 'N/A'}
                      </td>
                      <td className="px-6 py-4 font-bold text-slate-900">
                        {row.student_name}
                      </td>
                      <td className="px-6 py-4 text-slate-600 font-semibold">
                        {row.total_days}
                      </td>
                      <td className="px-6 py-4 font-bold text-emerald-600">
                        {row.present}
                      </td>
                      <td className="px-6 py-4 font-bold text-red-600">
                        {row.absent}
                      </td>
                      <td className="px-6 py-4 font-bold text-amber-600">
                        {row.late}
                      </td>
                      <td className="px-6 py-4 font-bold text-blue-600">
                        {row.leave}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider ${getPercentageBadge(row.percentage)}`}>
                          {row.percentage}%
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>
  );
};

export default AttendanceReport;
